import React, { useEffect, useState, useCallback } from 'react';
import SEO from '../../components/SEO';
import AdminLayout from '../../components/admin/AdminLayout';
import { useSidebar } from '../../contexts/SidebarContext';
import api from '../../utils/api';
import { showSuccess, showError } from '../../utils/toast';
import {
  Building2,
  Plus,
  Search,
  Trash2,
  Edit2,
  RefreshCw,
  X,
  Mail,
  MapPin,
  Download,
  ChevronLeft,
  ChevronRight,
  Shield,
  CheckCircle2,
  XCircle
} from 'lucide-react';

const DEPARTMENTS = [
  'Administration',
  'Police & Law',
  'Revenue & Tehsil',
  'Health & Medical',
  'Rural & Panchayat',
  'Municipal Services',
  'Electricity & Power',
  'Public Works & Water',
  'Education & Colleges',
  'Agriculture & Food',
  'Social Welfare & Others'
];

const COMMON_AREAS = [
  'Fatehabad',
  'Ratia',
  'Tohana',
  'Bhattu Kalan',
  'Bhuna',
  'Jakhal',
  'Kulan',
  'Gorakhpur',
  'District Level'
];

const DEPARTMENT_THEMES = {
  'Administration': 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800',
  'Police & Law': 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/40 dark:text-red-300 dark:border-red-800',
  'Revenue & Tehsil': 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800',
  'Health & Medical': 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800',
  'Rural & Panchayat': 'bg-lime-50 text-lime-800 border-lime-200 dark:bg-lime-950/40 dark:text-lime-300 dark:border-lime-800',
  'Municipal Services': 'bg-cyan-50 text-cyan-800 border-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300 dark:border-cyan-800',
  'Electricity & Power': 'bg-yellow-50 text-yellow-800 border-yellow-200 dark:bg-yellow-950/40 dark:text-yellow-300 dark:border-yellow-800',
  'Public Works & Water': 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-800',
  'Education & Colleges': 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-800',
  'Agriculture & Food': 'bg-teal-50 text-teal-800 border-teal-200 dark:bg-teal-950/40 dark:text-teal-300 dark:border-teal-800',
  'Social Welfare & Others': 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800'
};

const EMPTY_OFFICER_FORM = {
  serialNo: '',
  name: '',
  designation: '',
  department: 'Administration',
  area: 'Fatehabad',
  contactNo: '',
  officePhone: '',
  resiPhone: '',
  email: '',
  address: '',
  order: 0,
  isActive: true
};

const AdminOfficersContent = () => {
  const { isCollapsed } = useSidebar();
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCount: 0,
    activeCount: 0,
    inactiveCount: 0,
    departmentsCount: DEPARTMENTS.length
  });

  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pages: 1,
    limit: 25
  });

  // Filter states
  const [search, setSearch] = useState('');
  const [selectedDept, setSelectedDept] = useState('all');
  const [selectedArea, setSelectedArea] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [availableAreas, setAvailableAreas] = useState([]);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_OFFICER_FORM);
  const [submitting, setSubmitting] = useState(false);

  const fetchOfficers = useCallback(async (targetPage = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: targetPage,
        limit: 25
      });

      if (search.trim()) params.set('search', search.trim());
      if (selectedDept && selectedDept !== 'all') params.set('department', selectedDept);
      if (selectedArea && selectedArea !== 'all') params.set('area', selectedArea);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);

      const res = await api.get(`/officers/admin/list?${params.toString()}`);
      setOfficers(res.data.officers || []);
      setPagination(res.data.pagination || { total: 0, page: 1, pages: 1, limit: 25 });
      if (res.data.stats) setStats(res.data.stats);
      if (res.data.areas) setAvailableAreas(res.data.areas);
    } catch (err) {
      console.error('Error fetching admin officers list:', err);
      showError(err.response?.data?.message || 'Failed to load officers list');
    } finally {
      setLoading(false);
    }
  }, [search, selectedDept, selectedArea, statusFilter]);

  useEffect(() => {
    fetchOfficers(1);
  }, [fetchOfficers]);

  const openCreateModal = () => {
    setEditingId(null);
    setFormData({
      ...EMPTY_OFFICER_FORM,
      serialNo: stats.totalCount + 1,
      order: stats.totalCount + 1
    });
    setShowModal(true);
  };

  const openEditModal = (officer) => {
    setEditingId(officer._id);
    setFormData({
      serialNo: officer.serialNo || '',
      name: officer.name || '',
      designation: officer.designation || '',
      department: officer.department || 'Administration',
      area: officer.area || 'Fatehabad',
      contactNo: officer.contactNo || '',
      officePhone: officer.officePhone || '',
      resiPhone: officer.resiPhone || '',
      email: officer.email || '',
      address: officer.address || '',
      order: officer.order !== undefined ? officer.order : 0,
      isActive: officer.isActive !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showError('Please enter officer name');
      return;
    }
    if (!formData.designation.trim()) {
      showError('Please enter officer designation');
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/officers/${editingId}`, formData);
        showSuccess('Officer details updated successfully!');
      } else {
        await api.post('/officers', formData);
        showSuccess('New officer added successfully!');
      }
      setShowModal(false);
      fetchOfficers(pagination.page);
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to save officer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (officer) => {
    try {
      const nextStatus = !officer.isActive;
      await api.patch(`/officers/${officer._id}/status`, { isActive: nextStatus });
      showSuccess(`Officer marked as ${nextStatus ? 'Active' : 'Inactive'}`);
      setOfficers(prev => prev.map(o => o._id === officer._id ? { ...o, isActive: nextStatus } : o));
      setStats(prev => ({
        ...prev,
        activeCount: nextStatus ? prev.activeCount + 1 : prev.activeCount - 1,
        inactiveCount: nextStatus ? prev.inactiveCount - 1 : prev.inactiveCount + 1
      }));
    } catch (err) {
      showError('Failed to update officer status');
    }
  };

  const handleDelete = async (officer) => {
    if (!window.confirm(`Are you sure you want to delete "${officer.name}" (${officer.designation})?`)) {
      return;
    }

    try {
      await api.delete(`/officers/${officer._id}`);
      showSuccess('Officer removed successfully');
      setOfficers(prev => prev.filter(o => o._id !== officer._id));
      setStats(prev => ({
        ...prev,
        totalCount: Math.max(0, prev.totalCount - 1),
        activeCount: officer.isActive ? Math.max(0, prev.activeCount - 1) : prev.activeCount,
        inactiveCount: !officer.isActive ? Math.max(0, prev.inactiveCount - 1) : prev.inactiveCount
      }));
    } catch (err) {
      showError(err.response?.data?.message || 'Failed to delete officer');
    }
  };

  const handleExportCSV = () => {
    if (!officers.length) {
      showError('No officers to export');
      return;
    }

    const headers = [
      'Serial No',
      'Name',
      'Designation',
      'Department',
      'Area',
      'Mobile / Contact',
      'Office Phone',
      'Residence Phone',
      'Email',
      'Address',
      'Status'
    ];

    const rows = officers.map(o => [
      o.serialNo || '',
      `"${(o.name || '').replace(/"/g, '""')}"`,
      `"${(o.designation || '').replace(/"/g, '""')}"`,
      `"${(o.department || '').replace(/"/g, '""')}"`,
      `"${(o.area || '').replace(/"/g, '""')}"`,
      `"${(o.contactNo || '').replace(/"/g, '""')}"`,
      `"${(o.officePhone || '').replace(/"/g, '""')}"`,
      `"${(o.resiPhone || '').replace(/"/g, '""')}"`,
      `"${(o.email || '').replace(/"/g, '""')}"`,
      `"${(o.address || '').replace(/"/g, '""')}"`,
      o.isActive ? 'Active' : 'Inactive'
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `officers_directory_${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSuccess('Officers exported to CSV');
  };

  return (
    <>
      <SEO
        title="Admin - Officers Directory Management | Digital Fatehabad"
        description="Manage district administration officers, contact numbers and departments"
        noindex
      />

      <main
        className={`flex-1 transition-all duration-300 min-h-screen bg-gray-50/60 dark:bg-gray-950 ${
          isCollapsed ? 'ml-16' : 'ml-64'
        }`}
      >
        <div className="container-custom py-6 md:py-10 max-w-7xl mx-auto px-4">
          
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-600 text-white flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white flex items-center gap-2">
                  Officers Directory Management
                  <span className="text-xs px-2.5 py-0.5 rounded-full bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-800">
                    Govt Contacts
                  </span>
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Control, add, edit, or update government & administrative officers telephone contacts across Fatehabad.
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2.5 flex-wrap">
              <button
                onClick={() => fetchOfficers(pagination.page)}
                disabled={loading}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium transition-colors shadow-sm disabled:opacity-50"
                title="Refresh Officers"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>

              <button
                onClick={handleExportCSV}
                disabled={officers.length === 0}
                className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium transition-colors shadow-sm disabled:opacity-50"
                title="Export to CSV"
              >
                <Download className="w-4 h-4 text-emerald-600" />
                Export CSV
              </button>

              <button
                id="admin-add-officer-btn"
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all shadow-md hover:shadow-lg active:scale-95 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add New Officer
              </button>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5 mb-8">
            <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Total Officers</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.totalCount.toLocaleString()}</p>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Active Listed</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.activeCount.toLocaleString()}</p>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-rose-50 dark:bg-rose-950/50 text-rose-600 dark:text-rose-400 flex items-center justify-center flex-shrink-0">
                <XCircle className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Hidden / Inactive</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.inactiveCount.toLocaleString()}</p>
              </div>
            </div>

            <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm flex items-center gap-3.5">
              <div className="w-11 h-11 rounded-xl bg-purple-50 dark:bg-purple-950/50 text-purple-600 dark:text-purple-400 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Departments</p>
                <p className="text-xl font-bold text-gray-900 dark:text-white">{stats.departmentsCount}</p>
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="p-4 rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3.5">
              {/* Search */}
              <div className="lg:col-span-5 relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search officer name, designation, phone, or office..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-800 transition-all"
                />
              </div>

              {/* Department Filter */}
              <div className="lg:col-span-3">
                <select
                  value={selectedDept}
                  onChange={(e) => setSelectedDept(e.target.value)}
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                >
                  <option value="all">All Departments</option>
                  {DEPARTMENTS.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </select>
              </div>

              {/* Area Filter */}
              <div className="lg:col-span-2">
                <select
                  value={selectedArea}
                  onChange={(e) => setSelectedArea(e.target.value)}
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                >
                  <option value="all">All Areas</option>
                  {availableAreas.map(area => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div className="lg:col-span-2">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-3.5 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </select>
              </div>
            </div>
          </div>

          {/* Officers Table */}
          <div className="rounded-2xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
            {loading ? (
              <div className="p-16 flex flex-col items-center justify-center">
                <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Loading officers directory...</p>
              </div>
            ) : officers.length === 0 ? (
              <div className="p-16 text-center">
                <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4 text-gray-400">
                  <Building2 className="w-8 h-8 opacity-40" />
                </div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  No Officers Found
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
                  {search || selectedDept !== 'all' || selectedArea !== 'all' || statusFilter !== 'all'
                    ? 'No records match your filter criteria. Try resetting filters.'
                    : 'No officers currently listed in the database.'}
                </p>
                {(search || selectedDept !== 'all' || selectedArea !== 'all' || statusFilter !== 'all') && (
                  <button
                    onClick={() => {
                      setSearch('');
                      setSelectedDept('all');
                      setSelectedArea('all');
                      setStatusFilter('all');
                    }}
                    className="mt-4 px-4 py-2 rounded-xl bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 text-gray-700 dark:text-gray-300 text-xs font-semibold"
                  >
                    Reset Filters
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      <th className="py-3.5 px-4 w-12 text-center">#</th>
                      <th className="py-3.5 px-4">Officer & Designation</th>
                      <th className="py-3.5 px-4">Department & Area</th>
                      <th className="py-3.5 px-4">Contact Numbers</th>
                      <th className="py-3.5 px-4">Office & Address</th>
                      <th className="py-3.5 px-4 text-center">Status</th>
                      <th className="py-3.5 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-xs">
                    {officers.map((officer) => {
                      const deptTheme = DEPARTMENT_THEMES[officer.department] || 'bg-gray-100 text-gray-700 border-gray-200';

                      return (
                        <tr
                          key={officer._id}
                          className="hover:bg-gray-50/80 dark:hover:bg-gray-800/50 transition-colors"
                        >
                          {/* Serial */}
                          <td className="py-3.5 px-4 text-center whitespace-nowrap">
                            <span className="font-mono text-xs font-semibold text-gray-400 dark:text-gray-500">
                              #{officer.serialNo || officer.order || '-'}
                            </span>
                          </td>

                          {/* Officer Details */}
                          <td className="py-3.5 px-4 min-w-[200px]">
                            <div className="flex flex-col gap-0.5">
                              <span className="font-bold text-sm text-gray-900 dark:text-white">
                                {officer.name}
                              </span>
                              <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">
                                {officer.designation}
                              </span>
                            </div>
                          </td>

                          {/* Department & Area */}
                          <td className="py-3.5 px-4 min-w-[190px]">
                            <div className="flex flex-col gap-1.5 items-start">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold border ${deptTheme}`}>
                                {officer.department}
                              </span>
                              {officer.area && (
                                <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                                  <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                  {officer.area}
                                </span>
                              )}
                            </div>
                          </td>

                          {/* Contact Numbers */}
                          <td className="py-3.5 px-4 min-w-[200px]">
                            <div className="flex flex-col gap-1">
                              {officer.contactNo && (
                                <div className="flex items-center gap-1.5 text-xs font-mono font-medium text-gray-900 dark:text-white">
                                  <span className="text-[10px] uppercase font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-1 rounded">Mob</span>
                                  <a href={`tel:${officer.contactNo.split(/[,/]/)[0]?.trim()}`} className="hover:underline">
                                    {officer.contactNo}
                                  </a>
                                </div>
                              )}
                              {officer.officePhone && (
                                <div className="flex items-center gap-1.5 text-xs font-mono text-gray-600 dark:text-gray-300">
                                  <span className="text-[10px] uppercase font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/40 px-1 rounded">Off</span>
                                  <a href={`tel:${officer.officePhone.split(/[,/]/)[0]?.trim()}`} className="hover:underline">
                                    {officer.officePhone}
                                  </a>
                                </div>
                              )}
                              {officer.resiPhone && (
                                <div className="flex items-center gap-1.5 text-[11px] font-mono text-gray-500 dark:text-gray-400">
                                  <span className="text-[9px] uppercase font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/40 px-1 rounded">Res</span>
                                  <span>{officer.resiPhone}</span>
                                </div>
                              )}
                              {!officer.contactNo && !officer.officePhone && !officer.resiPhone && (
                                <span className="text-gray-400 text-xs italic">No phone listed</span>
                              )}
                            </div>
                          </td>

                          {/* Office & Address */}
                          <td className="py-3.5 px-4 max-w-[200px]">
                            <div className="flex flex-col gap-0.5">
                              {officer.email && (
                                <div className="flex items-center gap-1 text-[11px] text-gray-600 dark:text-gray-300 truncate" title={officer.email}>
                                  <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                  <span className="truncate">{officer.email}</span>
                                </div>
                              )}
                              {officer.address && (
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2" title={officer.address}>
                                  {officer.address}
                                </p>
                              )}
                              {!officer.email && !officer.address && (
                                <span className="text-gray-400 text-[11px]">-</span>
                              )}
                            </div>
                          </td>

                          {/* Status Toggle */}
                          <td className="py-3.5 px-4 text-center whitespace-nowrap">
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(officer)}
                              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-all ${
                                officer.isActive !== false
                                  ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 hover:bg-emerald-100'
                                  : 'bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {officer.isActive !== false ? (
                                <>
                                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                  Active
                                </>
                              ) : (
                                <>
                                  <XCircle className="w-3.5 h-3.5 text-gray-400" />
                                  Hidden
                                </>
                              )}
                            </button>
                          </td>

                          {/* Actions */}
                          <td className="py-3.5 px-4 text-right whitespace-nowrap">
                            <div className="inline-flex items-center gap-1.5">
                              <button
                                onClick={() => openEditModal(officer)}
                                className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors"
                                title="Edit Officer"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(officer)}
                                className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
                                title="Delete Officer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {pagination.pages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/40 dark:bg-gray-800/20 text-xs">
                <span className="text-gray-500 dark:text-gray-400">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                  <strong className="text-gray-900 dark:text-white font-bold">{pagination.total}</strong> officers
                </span>

                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => fetchOfficers(pagination.page - 1)}
                    disabled={pagination.page <= 1 || loading}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <span className="px-3 py-1 font-semibold text-gray-700 dark:text-gray-300">
                    Page {pagination.page} of {pagination.pages}
                  </span>

                  <button
                    onClick={() => fetchOfficers(pagination.page + 1)}
                    disabled={pagination.page >= pagination.pages || loading}
                    className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Add / Edit Officer Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-900 rounded-2xl max-w-2xl w-full shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-5 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-bold text-lg leading-tight">
                    {editingId ? 'Edit Officer Details' : 'Add New Government Officer'}
                  </h2>
                  <p className="text-xs text-white/80">District Fatehabad Administrative Directory</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto text-xs">
              {/* Name & Designation */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    Officer Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g. Dr. Vivek Bharti, IAS"
                    className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    Designation / Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.designation}
                    onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                    placeholder="e.g. Deputy Commissioner"
                    className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Department & Area */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    Department <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.department}
                    onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                    className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {DEPARTMENTS.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    Area / Division
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      list="area-suggestions"
                      value={formData.area}
                      onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                      placeholder="e.g. Fatehabad, Ratia, Tohana"
                      className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <datalist id="area-suggestions">
                      {COMMON_AREAS.map((a) => (
                        <option key={a} value={a} />
                      ))}
                    </datalist>
                  </div>
                </div>
              </div>

              {/* Contact Numbers Row */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    Mobile / Contact No
                  </label>
                  <input
                    type="text"
                    value={formData.contactNo}
                    onChange={(e) => setFormData({ ...formData, contactNo: e.target.value })}
                    placeholder="e.g. 9812345678"
                    className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    Office Landline
                  </label>
                  <input
                    type="text"
                    value={formData.officePhone}
                    onChange={(e) => setFormData({ ...formData, officePhone: e.target.value })}
                    placeholder="e.g. 01667-230001"
                    className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    Residence Landline
                  </label>
                  <input
                    type="text"
                    value={formData.resiPhone}
                    onChange={(e) => setFormData({ ...formData, resiPhone: e.target.value })}
                    placeholder="e.g. 01667-230002"
                    className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                  />
                </div>
              </div>

              {/* Email & Office Address */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    Official Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="e.g. dc-ftb@hry.nic.in"
                    className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                    Serial No / Display Order
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={formData.serialNo}
                      onChange={(e) => setFormData({ ...formData, serialNo: e.target.value })}
                      placeholder="Serial #"
                      className="w-1/2 px-3.5 py-2.5 text-xs rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      type="number"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: e.target.value })}
                      placeholder="Order #"
                      className="w-1/2 px-3.5 py-2.5 text-xs rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-1.5">
                  Office Address
                </label>
                <textarea
                  rows="2"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="e.g. Mini Secretariat, Fatehabad, Haryana"
                  className="w-full px-3.5 py-2.5 text-xs rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              {/* Status Toggle */}
              <div className="flex items-center gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, isActive: !formData.isActive })}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out ${
                    formData.isActive ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-700'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      formData.isActive ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
                <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                  {formData.isActive ? '✅ Listed & Visible on public directory' : '⛔ Hidden from public directory'}
                </span>
              </div>

              {/* Modal Buttons */}
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100 dark:border-gray-800">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 text-xs font-semibold rounded-xl border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-2.5 text-xs font-bold rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white transition-all shadow-md disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : editingId ? 'Update Officer' : 'Save Officer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const AdminOfficers = () => (
  <AdminLayout>
    <AdminOfficersContent />
  </AdminLayout>
);

export default AdminOfficers;
